import { AxiosError, AxiosResponse } from 'axios';
import { Button } from 'components/universal/button';
import { useUser } from 'hooks/useUser';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { deleteUsersAxios } from 'services/registration';
import { APIErrors } from 'types/api';
import { employeesInterface } from 'views/employee-management';
import cls from './deleteUsersForm.module.scss';

export interface DeleteUsersValue {
    ids: number[];
}

interface DeleteUsersFormFormInterface {
    users: employeesInterface[];
    onCancel: (boolean) => void;
}

export const DeleteUsersForm = ({
    users,
    onCancel
}: DeleteUsersFormFormInterface) => {
    const { t } = useTranslation();
    const [userNames, setUserNames] = useState<string>('');
    const { userData } = useUser();
    
    const {
        register,
        handleSubmit,
        setError,
        setValue,
        formState: { errors },
    } = useForm<DeleteUsersValue>({
        defaultValues: {
            ids: [],
        },
    });

    useEffect(() => {
        const userIdsString = users
            .map(user => user.id)
            .filter(id => id != null);
        
        const userNamesString = users
            .map(user => `${user.first_name} ${user.last_name}`)
            .filter(fullName => fullName.trim() !== '')
            .join(', ');

        setUserNames(userNamesString);
        setValue('ids', userIdsString);
    }, [users, setValue]);

    const onSubmit = (formObject: DeleteUsersValue) => {
        deleteUsers.mutate(formObject);
    };

    const deleteUsers = useMutation<
        AxiosResponse<{ message: string }>,
        AxiosError<APIErrors>,
        DeleteUsersValue
    >((data) => deleteUsersAxios(data), {
        onSuccess: (data) => {
            if (data.status === 200) {
                toast.success(t('employeeManagement.deleteSuccessMessage'));
                onCancel(true);
            }
        },
        onError: (error) => {
            const formErrors = error?.response?.data?.errors;
            if (formErrors) {
                Object.entries(formErrors).forEach(([key, value]) => {
                    setError(key as keyof DeleteUsersValue, {
                        message: value?.[0],
                    });
                });
            }
        },
    });

    return (
        <>
            <h3 className={cls.modalHeader}>{users.length === 1 ? t('employeeManagement.deleteUserModalLabel_one') : t('employeeManagement.deleteUserModalLabel')}</h3>
            <p className={cls.modalDescription}>{users.length === 1 ? t('employeeManagement.deleteUserModalDescription_one') : t('employeeManagement.deleteUserModalDescription')}</p>
            <p className={cls.modalNames}><strong>{userNames}</strong></p>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <input {...register('ids')} type="hidden" />
                <div className={cls.modalButtonWrapper}>
                    <Button className={cls.modalButton} styleType="border" type="button" onClick={onCancel}>
                        {t('employeeManagement.cancel')}
                    </Button>
                    <Button className={cls.modalButton} type="submit">
                        {t('employeeManagement.delete')}
                    </Button>
                </div>
            </form>
        </>
    );
};
